import { deleteToken, getMessaging, getToken, isSupported, onMessage, type MessagePayload } from 'firebase/messaging';
import { app, firebaseClientConfig } from './config';
import { disableNotificationDevice, saveNotificationDevice } from './repo';
import type { RuntimeImportMeta } from '../utils/runtimeEnv';

const viteEnv = ((import.meta as unknown as RuntimeImportMeta).env || {}) as Record<string, string | undefined>;
const deviceKey = 'pfs_notification_device_id';

const deviceId = () => {
  const existing = localStorage.getItem(deviceKey);
  if (existing) return existing;
  const next = `web_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem(deviceKey, next);
  return next;
};

const userAgentSummary = () => {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || 'web';
  return ua.length > 90 ? `${ua.slice(0, 90)}...` : ua;
};

export async function browserPushSupport() {
  const supported = typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
    && await isSupported().catch(() => false);
  return {
    supported,
    permission: typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
    vapidConfigured: !!viteEnv.VITE_FIREBASE_VAPID_KEY
  };
}

export async function enableBrowserPush(uid: string) {
  const support = await browserPushSupport();
  if (!support.supported) throw new Error('Browser notifications are not supported on this browser.');
  if (!support.vapidConfigured) throw new Error('VITE_FIREBASE_VAPID_KEY is missing. Add the Firebase Web Push certificate key and restart Vite.');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error(permission === 'denied' ? 'Browser notification permission is denied.' : 'Browser notification permission was not granted.');

  const params = new URLSearchParams({
    apiKey: firebaseClientConfig.apiKey || '',
    authDomain: firebaseClientConfig.authDomain || '',
    projectId: firebaseClientConfig.projectId || '',
    storageBucket: firebaseClientConfig.storageBucket || '',
    messagingSenderId: firebaseClientConfig.messagingSenderId || '',
    appId: firebaseClientConfig.appId || ''
  });
  const registration = await navigator.serviceWorker.register(`/firebase-messaging-sw.js?${params.toString()}`);
  const messaging = getMessaging(app);
  const token = await getToken(messaging, { vapidKey: viteEnv.VITE_FIREBASE_VAPID_KEY, serviceWorkerRegistration: registration });
  if (!token) throw new Error('Firebase did not return a browser notification token.');
  const id = deviceId();
  await saveNotificationDevice(uid, id, { token, platform: 'web', userAgentSummary: userAgentSummary() });
  return { deviceId: id, permission, tokenPresent: true };
}

export async function disableBrowserPush(uid: string) {
  const id = localStorage.getItem(deviceKey);
  if (await isSupported().catch(() => false)) {
    await deleteToken(getMessaging(app)).catch(() => undefined);
  }
  if (id) await disableNotificationDevice(uid, id);
}

export function listenForegroundPush(cb: (payload: MessagePayload) => void) {
  if (typeof window === 'undefined') return () => undefined;
  let unsubscribe: () => void = () => undefined;
  isSupported().then((supported) => {
    if (supported) unsubscribe = onMessage(getMessaging(app), cb);
  }).catch(() => undefined);
  return () => unsubscribe();
}
